import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { Form as AntForm, InputNumber, Switch, Select as AntSelect, Button, Spin } from 'antd';
import axios from 'axios';
import Swal from 'sweetalert2';
import { encryptBody, logMfaRequest, logMfaSuccess, logMfaError, normalizeStatusCode, mfaApi, unwrapMfaPayload } from './mfaApi';

const LABEL_COL = { span: 9, style: { whiteSpace: 'pre-wrap' as const } };

const BEHAVIOR_OPTIONS = [
  { value: true, label: 'Verrouiller le compte' },
  { value: false, label: 'Forcer le changement de mot de passe' },
];

interface RotationConfig {
  RotationDays: number;
  GraceWarningDays: number;
  LockAccountOnExpiry: boolean;
  RequireUppercase: boolean;
  RequireLowercase: boolean;
  RequireDigit: boolean;
  RequireSpecialChar: boolean;
  MinPasswordClasses: number;
  BlockCommonPasswords: boolean;
  Statut: string;
  ModifiedBy: string;
}

const defaultConfig: RotationConfig = {
  RotationDays: 90,
  GraceWarningDays: 7,
  LockAccountOnExpiry: false,
  RequireUppercase: true,
  RequireLowercase: true,
  RequireDigit: true,
  RequireSpecialChar: true,
  MinPasswordClasses: 3,
  BlockCommonPasswords: false,
  Statut: 'ACTIF',
  ModifiedBy: 'SYSTEM',
};

interface MfaPasswordRotationPanelProps {
  formLocked: boolean;
  headers: any;
  currentUser: any;
  saveRequest: number;
}

export default function MfaPasswordRotationPanel({ formLocked, headers, currentUser, saveRequest }: MfaPasswordRotationPanelProps) {
  const [config, setConfig] = useState<RotationConfig>(defaultConfig);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadPolicy(); }, []);
  useEffect(() => { if (saveRequest > 0) savePolicy(); }, [saveRequest]);

  function notifySuccess(text: string) {
    Swal.fire({ icon: 'success', title: 'Succès', text, confirmButtonColor: '#0d4a70' });
  }
  function notifyError(text: string) {
    Swal.fire({ icon: 'error', title: 'Erreur', text, confirmButtonColor: '#0d4a70' });
  }

  async function loadPolicy() {
    setLoading(true);
    try {
      logMfaRequest('loadRotationPolicy', mfaApi.passwordPolicyByRole('default'));
      const res = await axios.get(mfaApi.passwordPolicyByRole('default'), { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadRotationPolicy', res);
      const result = unwrapMfaPayload(parsed);
      if (normalizeStatusCode(parsed) < 300 && result && typeof result === 'object' && Object.keys(result).length > 0) {
        setConfig((current) => ({ ...current, ...result }));
      } else {
        setConfig((current) => ({ ...current, ModifiedBy: currentUser?.coduti ?? 'SYSTEM' }));
      }
    } catch (e) {
      logMfaError('loadRotationPolicy', '');
    } finally {
      setLoading(false);
    }
  }

  async function savePolicy() {
    setLoading(true);
    try {
      const payload = {
        RotationDays: Number(config.RotationDays),
        GraceWarningDays: Number(config.GraceWarningDays),
        LockAccountOnExpiry: Boolean(config.LockAccountOnExpiry),
        RequireUppercase: Boolean(config.RequireUppercase),
        RequireLowercase: Boolean(config.RequireLowercase),
        RequireDigit: Boolean(config.RequireDigit),
        RequireSpecialChar: Boolean(config.RequireSpecialChar),
        MinPasswordClasses: Number(config.MinPasswordClasses),
        BlockCommonPasswords: Boolean(config.BlockCommonPasswords),
        Statut: config.Statut || 'ACTIF',
        ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
      };
      logMfaRequest('saveRotationPolicy', mfaApi.passwordPolicyUpdate, payload);
      const res = await axios.put(mfaApi.passwordPolicyUpdate, encryptBody(payload), { headers, withCredentials: true });
      const parsed = logMfaSuccess('saveRotationPolicy', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess('Politique de rotation des mots de passe enregistrée.');
        loadPolicy();
      } else {
        notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement.");
      }
    } catch (e: any) {
      const parsed = logMfaError('saveRotationPolicy', '');
      notifyError(parsed?.Message ?? "Le serveur a refusé la transaction.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Spin spinning={loading}>
      <fieldset className="bordure1" style={{ marginTop: 16 }}>
        <legend style={{ color: '#116599' }}>Rotation et complexité des mots de passe</legend>
        <AntForm component="div" labelAlign="left" disabled={formLocked}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Fréquence de rotation (jours)" labelCol={LABEL_COL}>
                <InputNumber min={60} max={365} style={{ width: '100%' }} value={config.RotationDays}
                  onChange={(value) => setConfig({ ...config, RotationDays: Number(value) })} />
              </AntForm.Item>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Délai de grâce (jours)" labelCol={LABEL_COL}>
                <InputNumber min={1} max={90} style={{ width: '100%' }} value={config.GraceWarningDays}
                  onChange={(value) => setConfig({ ...config, GraceWarningDays: Number(value) })} />
              </AntForm.Item>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Comportement après expiration" labelCol={LABEL_COL}>
                <AntSelect style={{ width: '100%' }} value={config.LockAccountOnExpiry}
                  onChange={(value) => setConfig({ ...config, LockAccountOnExpiry: Boolean(value) })}
                  options={BEHAVIOR_OPTIONS} />
              </AntForm.Item>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Classes minimales requises" labelCol={LABEL_COL}>
                <InputNumber min={1} max={4} style={{ width: '100%' }} value={config.MinPasswordClasses}
                  onChange={(value) => setConfig({ ...config, MinPasswordClasses: Number(value) })} />
              </AntForm.Item>
            </Grid> */}
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Exiger majuscules" labelCol={LABEL_COL}>
                <Switch checked={config.RequireUppercase} disabled={formLocked}
                  onChange={(checked) => setConfig({ ...config, RequireUppercase: Boolean(checked) })} />
              </AntForm.Item>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Exiger minuscules" labelCol={LABEL_COL}>
                <Switch checked={config.RequireLowercase} disabled={formLocked}
                  onChange={(checked) => setConfig({ ...config, RequireLowercase: Boolean(checked) })} />
              </AntForm.Item>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Exiger chiffres" labelCol={LABEL_COL}>
                <Switch checked={config.RequireDigit} disabled={formLocked}
                  onChange={(checked) => setConfig({ ...config, RequireDigit: Boolean(checked) })} />
              </AntForm.Item>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <AntForm.Item className="bordure" label="Exiger caractères spéciaux" labelCol={LABEL_COL}>
                <Switch checked={config.RequireSpecialChar} disabled={formLocked}
                  onChange={(checked) => setConfig({ ...config, RequireSpecialChar: Boolean(checked) })} />
              </AntForm.Item>
            </Grid>


          </Grid>
        </AntForm>
      </fieldset>
    </Spin>
  );
}
